
import { Button } from "@/components/ui/button";
import { FcGoogle } from "react-icons/fc";
import { ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/utils/logger';
import { isInstalledApp } from "@/lib/platform";

export function AuthProviders() {
  const { toast } = useToast();
  const installed = isInstalledApp();

  const handleGoogleSignIn = async () => {
    try {
      const redirectTo = window.location.origin + window.location.pathname;

      // Installed home-screen apps have an isolated storage jar. The PKCE
      // code_verifier is written here, so the OAuth callback MUST come back to
      // this same webview. Opening Safari (window.open) strands the code in a
      // context that has no verifier — the app then never signs in.
      // A top-level navigation keeps the whole round-trip in-context.
      if (installed) {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo, skipBrowserRedirect: true },
        });
        if (error) throw error;
        if (data?.url) {
          window.location.assign(data.url);
        }
        return;
      }

      toast({
        title: "Sending you to Google",
        description: "Approve access and you'll come right back to GoalDigger.",
        duration: 2500,
      });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });

      if (error) throw error;
    } catch (error: any) {
      logger.error("❌ Google sign-in error:", error);
      toast({
        title: "Error signing in with Google",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full space-y-2">
      <Button
        variant="outline"
        onClick={handleGoogleSignIn}
        className="w-full flex items-center justify-center gap-2"
        aria-label="Continue with Google"
      >
        <FcGoogle className="h-5 w-5" aria-hidden="true" />
        Continue with Google
      </Button>
      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground px-1">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          {installed
            ? <>Google opens inside GoalDigger and returns you here automatically. If it ever opens Safari, use Face&nbsp;ID or the email code below instead.</>
            : <>You'll be securely redirected to Google. After you approve, you'll come right back to <strong>GoalDigger</strong>.</>}
        </span>
      </p>
    </div>
  );
}
